import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  activateAttendancePolicy,
  ApiError,
  fetchActivePolicy,
  fetchWorkLocations,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import {
  bodiKebijakan,
  formDariKebijakan,
  KEBIJAKAN_BAWAAN,
  NAMA_HARI,
  periksaKebijakan,
  type FormKebijakan,
} from '../lib/kebijakan-absensi';
import { ErrorCard, LoadingState } from '../states';

/**
 * Kebijakan absensi — PINTU HARI PERTAMA sebuah organisasi.
 *
 * Tanpa kebijakan aktif, server menolak SETIAP absen masuk dengan `ATT-001`, dan aplikasinya tidak
 * dapat dipakai sama sekali. Sampai layar ini ada, satu-satunya jalan membuatnya adalah
 * menjalankan `provision.seed.ts` terhadap basis data produksi — yakni seorang insinyur dengan
 * kredensial produksi, untuk setiap pelanggan baru.
 *
 * Karena itu tujuan layar ini bukan "menampilkan semua setelan", melainkan **membuat organisasi
 * baru dapat mulai bekerja tanpa memahami satu pun setelan lanjutan**. Nilai bawaannya diambil
 * dari yang dipakai provisioning sungguhan, dan setelan lanjutan disembunyikan sampai diminta.
 */
export function KebijakanAbsensiPage(): ReactNode {
  const session = useSession();

  const aktif = useAsync(() => fetchActivePolicy(session.auth), [session.auth]);
  /*
   * Lokasi kerja dibaca BUKAN untuk ditampilkan, melainkan karena satu aturan pemeriksaan
   * bergantung padanya: organisasi tanpa lokasi kerja yang juga melarang absen di luar geofence
   * akan menolak setiap absen masuk. Lihat `periksaKebijakan`.
   */
  const lokasi = useAsync(() => fetchWorkLocations(session.auth), [session.auth]);

  const [form, setForm] = useState<FormKebijakan | null>(null);
  const [lanjutan, setLanjutan] = useState(false);
  const [mengirim, setMengirim] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);
  const [berhasil, setBerhasil] = useState(false);

  const memuat = aktif.state.status === 'loading' || lokasi.state.status === 'loading';

  // Formulir baru diisi setelah kebijakan aktif diketahui — menyunting dimulai dari yang berlaku.
  const nilai =
    form ??
    (aktif.state.status === 'ready' && aktif.state.data
      ? formDariKebijakan(aktif.state.data)
      : KEBIJAKAN_BAWAAN);

  const jumlahLokasi = lokasi.state.status === 'ready' ? lokasi.state.data.length : 0;
  const masalah = periksaKebijakan(nilai, jumlahLokasi);
  const ubah = (bagian: Partial<FormKebijakan>): void => {
    setBerhasil(false);
    setForm({ ...nilai, ...bagian });
  };

  async function simpan(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (masalah.length > 0) return;
    setMengirim(true);
    setGalat(null);
    try {
      await activateAttendancePolicy(session.auth, bodiKebijakan(nilai));
      setBerhasil(true);
      setForm(null);
      aktif.reload();
    } catch (caught: unknown) {
      setGalat(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setMengirim(false);
    }
  }

  return (
    <>
      <section className="card">
        <h2>Kebijakan absensi</h2>
        <p className="muted small">
          Menentukan hari dan jam kerja, serta bagaimana absen di luar lokasi kerja diperlakukan.
          Tanpa kebijakan aktif, absen masuk tidak dapat dilakukan sama sekali.
        </p>
      </section>

      {memuat ? <LoadingState label="Memuat kebijakan…" /> : null}
      {aktif.state.status === 'error' ? (
        <ErrorCard error={aktif.state.error} onRetry={aktif.reload} />
      ) : null}

      {aktif.state.status === 'ready' && aktif.state.data === null ? (
        <section className="card perlu-perhatian">
          <h3>Belum ada kebijakan aktif</h3>
          <p>
            Absen masuk akan ditolak sampai kebijakan disimpan. Nilai di bawah sudah terisi dengan
            setelan yang lazim — Anda dapat langsung menyimpannya, lalu menyesuaikannya nanti.
          </p>
        </section>
      ) : null}

      {berhasil ? (
        <section className="card">
          <p>Kebijakan tersimpan dan langsung berlaku.</p>
        </section>
      ) : null}

      {galat ? <ErrorCard error={galat} /> : null}

      {!memuat && aktif.state.status === 'ready' ? (
        <form className="card" onSubmit={(e) => void simpan(e)}>
          <label>Hari kerja</label>
          {/*
            Lencana, bukan daftar centang bertumpuk: layar ini dibuka di ponsel, dan tujuh kotak
            centang bertumpuk menghabiskan satu layar penuh untuk satu pertanyaan.
          */}
          <div className="row hari-kerja">
            {[1, 2, 3, 4, 5, 6, 7].map((h) => {
              const dipilih = nilai.workDays.includes(h);
              return (
                <button
                  type="button"
                  key={h}
                  className={`chip${dipilih ? ' active' : ''}`}
                  aria-pressed={dipilih}
                  onClick={() =>
                    ubah({
                      workDays: dipilih
                        ? nilai.workDays.filter((x) => x !== h)
                        : [...nilai.workDays, h],
                    })
                  }
                >
                  {NAMA_HARI[h]}
                </button>
              );
            })}
          </div>

          {/*
            Item 8, review 12 Sep 2026 — PEMBALIKAN keputusan sebelumnya. Ditaruh langsung di
            bawah pemilih hari kerja karena keduanya menjawab pertanyaan yang sama: apa yang
            terjadi pada hari-hari yang TIDAK dipilih di atas.
          */}
          <label className="row baris-sakelar">
            <input
              type="checkbox"
              checked={nilai.allowCheckInOnNonWorkDays}
              onChange={(e) => ubah({ allowCheckInOnNonWorkDays: e.target.checked })}
            />
            <span>Izinkan absen masuk di hari yang bukan hari kerja</span>
          </label>

          <div className="row">
            <div>
              <label htmlFor="mulai">Jam mulai</label>
              <input
                id="mulai"
                type="time"
                value={nilai.workStartTime}
                onChange={(e) => ubah({ workStartTime: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="selesai">Jam selesai</label>
              <input
                id="selesai"
                type="time"
                value={nilai.workEndTime}
                onChange={(e) => ubah({ workEndTime: e.target.value })}
              />
            </div>
          </div>

          <label htmlFor="tz">Zona waktu</label>
          <input
            id="tz"
            value={nilai.timezone}
            onChange={(e) => ubah({ timezone: e.target.value })}
          />

          <label className="row baris-sakelar">
            <input
              type="checkbox"
              checked={nilai.allowCheckInOutsideGeofence}
              onChange={(e) => ubah({ allowCheckInOutsideGeofence: e.target.checked })}
            />
            <span>Izinkan absen di luar lokasi kerja</span>
          </label>

          {jumlahLokasi === 0 ? (
            <p className="muted small">
              Belum ada lokasi kerja terdaftar.{' '}
              <Link to="/work-locations">Tambahkan lokasi kerja</Link> bila titiknya sudah
              diketahui.
            </p>
          ) : null}

          {/*
            Setelan lanjutan disembunyikan. Organisasi baru harus dapat menekan Simpan tanpa
            memutuskan toleransi keterlambatan maupun batas akurasi GPS lebih dulu — keduanya punya
            nilai bawaan yang sudah dipakai organisasi sungguhan.
          */}
          <button type="button" className="tautan-kecil" onClick={() => setLanjutan((v) => !v)}>
            {lanjutan ? 'Sembunyikan setelan lanjutan' : 'Setelan lanjutan'}
          </button>

          {lanjutan ? (
            <div className="lanjutan">
              <label htmlFor="toleransi">Toleransi terlambat (menit)</label>
              <input
                id="toleransi"
                type="number"
                min={0}
                max={240}
                value={nilai.lateToleranceMinutes}
                onChange={(e) => ubah({ lateToleranceMinutes: Number(e.target.value) })}
              />

              <label htmlFor="gps">Akurasi GPS minimum (meter)</label>
              <input
                id="gps"
                type="number"
                min={1}
                max={5000}
                value={nilai.minGpsAccuracyMeters}
                onChange={(e) => ubah({ minGpsAccuracyMeters: Number(e.target.value) })}
              />

              <label htmlFor="selfie">Swafoto saat absen</label>
              <select
                id="selfie"
                value={nilai.selfiePolicy}
                onChange={(e) =>
                  ubah({ selfiePolicy: e.target.value as FormKebijakan['selfiePolicy'] })
                }
              >
                <option value="OPTIONAL">Opsional</option>
                <option value="REQUIRED">Wajib</option>
                <option value="DISABLED">Tidak dipakai</option>
              </select>

              <label className="row baris-sakelar">
                <input
                  type="checkbox"
                  checked={nilai.outsideGeofenceRequiresApproval}
                  onChange={(e) => ubah({ outsideGeofenceRequiresApproval: e.target.checked })}
                />
                <span>Absen di luar lokasi butuh persetujuan manajer</span>
              </label>

              <label className="row baris-sakelar">
                <input
                  type="checkbox"
                  checked={nilai.outsideGeofenceRequiresReason}
                  onChange={(e) => ubah({ outsideGeofenceRequiresReason: e.target.checked })}
                />
                <span>Absen di luar lokasi harus menyertakan alasan</span>
              </label>

              <label className="row baris-sakelar">
                <input
                  type="checkbox"
                  checked={nilai.fieldWorkGeofenceExempt}
                  onChange={(e) => ubah({ fieldWorkGeofenceExempt: e.target.checked })}
                />
                <span>Tugas lapangan dikecualikan dari geofence</span>
              </label>

              <label htmlFor="tutup">Batas tutup hari (jam)</label>
              <input
                id="tutup"
                type="number"
                min={0}
                max={72}
                value={nilai.dayCloseCutoffHours}
                onChange={(e) => ubah({ dayCloseCutoffHours: Number(e.target.value) })}
              />

              <label htmlFor="awal">Absen masuk paling awal (menit sebelum jam mulai)</label>
              <input
                id="awal"
                type="number"
                min={0}
                max={240}
                value={nilai.maxEarlyCheckInMinutes}
                onChange={(e) => ubah({ maxEarlyCheckInMinutes: Number(e.target.value) })}
              />
            </div>
          ) : null}

          {masalah.length > 0 ? (
            <ul className="list ringkas" role="alert">
              {masalah.map((m) => (
                <li key={m.bidang} className="warn-text">
                  {m.pesan}
                </li>
              ))}
            </ul>
          ) : null}

          <button
            type="submit"
            className="button-primary big"
            disabled={mengirim || masalah.length > 0}
          >
            {mengirim ? 'Menyimpan…' : 'Simpan kebijakan'}
          </button>
        </form>
      ) : null}
    </>
  );
}
